"use client";

import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { AssetImage } from "@/components/optilase/asset-image";
import Link from "@/components/ui/intent-link";

import {
  absoluteAssetUrl,
  blogPostUrl,
  resolveCategoryName,
  type BlogCategory,
  type BlogPostStory,
  postCategories,
} from "@/lib/blog";
import type { Region } from "@/lib/region";

const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
const CATEGORY_KEY = "tf:blog-category";
const SCROLL_KEY = "tf:blog-scroll";

function rememberScroll() {
  sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
}

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BlogCard({ post, region }: { post: BlogPostStory; region: Region }) {
  const c = post.content || {};
  const href = blogPostUrl(post.slug, region);
  return (
    <li>
      <Link
        href={href}
        onClick={rememberScroll}
        className="group flex h-full flex-col rounded-2xl focus-visible:outline-offset-4"
      >
        <div className="bg-teal-02 relative aspect-29/20 w-full overflow-hidden rounded-2xl">
          <AssetImage
            src={absoluteAssetUrl(c.featured_image?.filename) || ""}
            focus={c.featured_image?.focus}
            alt={c.featured_image?.alt || post.name}
            fill
            sizes="(min-width: 1115px) 411px, (min-width: 768px) 50vw, 100vw"
            className="object-cover"
          />
        </div>
        <div className="flex flex-1 flex-col gap-6 py-5 pr-5">
          <h2 className="text-grey-08 leading-[1.3] font-medium">{c.title || post.name}</h2>
          <span className="text-teal-07 mt-auto font-bold group-hover:underline">Read more</span>
        </div>
      </Link>
    </li>
  );
}

export function BlogIndexPage({
  posts,
  region,
  categories: datasourceCategories = [],
}: {
  posts: BlogPostStory[];
  region: Region;
  categories?: BlogCategory[];
}) {
  const categories = useMemo(
    () => Array.from(new Set(posts.flatMap((p) => postCategories(p.content)))),
    [posts],
  );

  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useIsoLayoutEffect(() => {
    const saved = sessionStorage.getItem(CATEGORY_KEY);
    if (saved && categories.includes(saved)) {
      flushSync(() => setActiveCategory(saved));
    }
    const y = sessionStorage.getItem(SCROLL_KEY);
    if (y) {
      window.scrollTo(0, parseInt(y, 10) || 0);
      sessionStorage.removeItem(SCROLL_KEY);
    }
  }, [categories]);

  const selectCategory = (cat: string | null) => {
    setActiveCategory(cat);
    if (cat) sessionStorage.setItem(CATEGORY_KEY, cat);
    else sessionStorage.removeItem(CATEGORY_KEY);
  };

  const filtered = activeCategory
    ? posts.filter((p) => postCategories(p.content).includes(activeCategory))
    : posts;

  const flagged = filtered.find((p) => p.content?.featured === true);
  const featured = flagged ?? filtered[0];
  const rest = filtered.filter((p) => p.uuid !== featured?.uuid);

  return (
    <article>
      <section className="bg-teal-02 px-5 py-10 md:px-10 lg:px-20 lg:py-20">
        <div className="mx-auto w-full max-w-7xl">
          <div className="mx-auto max-w-180 text-center">
            <h1 className="text-h2 text-teal-08">Eye surgery blog &amp; vision care insights</h1>
            <p className="text-grey-07 mt-5 text-base leading-normal lg:text-xl">
              Our eye care blog brings together patient stories, expert guidance, and the latest
              news from Optilase. From laser and lens surgery guides and real patient journeys to
              clinic updates and aftercare advice, find the insights that matter most to your
              vision.
            </p>
          </div>

          {categories.length > 0 && (
            <div className="mx-auto mt-8 flex max-w-205 flex-wrap items-center justify-center gap-2 lg:mt-10">
              <button
                type="button"
                onClick={() => selectCategory(null)}
                aria-pressed={!activeCategory}
                className={`inline-flex h-10 cursor-pointer items-center rounded-full px-6 font-medium transition-colors ${
                  !activeCategory
                    ? "bg-teal-08 text-white"
                    : "text-teal-08 hover:bg-teal-01 bg-white"
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => selectCategory(cat)}
                  aria-pressed={activeCategory === cat}
                  className={`inline-flex h-10 cursor-pointer items-center rounded-full px-6 font-medium transition-colors ${
                    activeCategory === cat
                      ? "bg-teal-08 text-white"
                      : "text-teal-08 hover:bg-teal-01 bg-white"
                  }`}
                >
                  {resolveCategoryName(cat, datasourceCategories) || cat}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {filtered.length === 0 ? (
        <div className="bg-white px-5 py-10 md:px-10 lg:px-20 lg:py-20">
          <div className="mx-auto w-full max-w-7xl">
            <p className="border-grey-01 text-grey-06 rounded-2xl border border-dashed p-10 text-center">
              No posts in this category yet — check back soon.
            </p>
          </div>
        </div>
      ) : (
        <>
          {!activeCategory && featured && (
            <section className="bg-white px-5 pt-10 md:px-10 lg:px-20 lg:pt-20">
              <div className="mx-auto w-full max-w-7xl">
                <FeaturedCard post={featured} region={region} categories={datasourceCategories} />
                <hr className="border-grey-01 mt-10 lg:mt-20" />
              </div>
            </section>
          )}
          {(activeCategory ? filtered : rest).length > 0 && (
            <section className="bg-white px-5 py-10 md:px-10 lg:px-20 lg:py-20">
              <div className="mx-auto w-full max-w-7xl">
                <ul className="grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
                  {(activeCategory ? filtered : rest).map((post) => (
                    <BlogCard key={post.uuid} post={post} region={region} />
                  ))}
                </ul>
              </div>
            </section>
          )}
        </>
      )}
    </article>
  );
}

function FeaturedCard({
  post,
  region,
  categories = [],
}: {
  post: BlogPostStory;
  region: Region;
  categories?: BlogCategory[];
}) {
  const c = post.content || {};
  const href = blogPostUrl(post.slug, region);
  const category = c.category ? resolveCategoryName(c.category, categories) : "";

  return (
    <Link
      href={href}
      onClick={rememberScroll}
      className="group flex flex-col gap-8 rounded-2xl focus-visible:outline-offset-4 md:flex-row md:items-center lg:gap-15"
    >
      <div className="bg-teal-02 relative aspect-16/10 w-full overflow-hidden rounded-2xl md:flex-1 md:shrink-0 lg:h-100.75 lg:w-160 lg:flex-initial">
        <AssetImage
          src={absoluteAssetUrl(c.featured_image?.filename) || ""}
          focus={c.featured_image?.focus}
          alt={c.featured_image?.alt || post.name}
          fill
          sizes="(min-width: 1115px) 640px, (min-width: 768px) 50vw, 100vw"
          className="object-cover"
          priority
        />
      </div>
      <div className="flex flex-col gap-5 md:flex-1 md:shrink-0 lg:w-131.75 lg:flex-initial">
        {category && (
          <span className="text-grey-06 font-medium tracking-[-0.01em] uppercase">{category}</span>
        )}
        <h2 className="text-h3 text-teal-07">{c.title || post.name}</h2>
        <span className="text-grey-06 inline-flex w-fit items-center gap-2 font-bold group-hover:underline">
          Read story
          <ArrowIcon />
        </span>
      </div>
    </Link>
  );
}
